// Product page (sections/main-product.liquid).
// Gallery: only the chosen colour's photos show (when the section groups them); desktop keeps the grid rule (the first
// photo and a last photo that would be alone span both columns); phones scroll sideways, with progress bars kept in
// step by an IntersectionObserver. Hovering or focusing a swatch starts loading that colour's first photo.
// Choices: DE.Picker (variant-picker.js). A colour change clears the size. The page never writes the address (no
// history.replaceState or pushState: Meta's pixel counts each address change as a page view); a ?variant= link is
// drawn by the section. Every change updates the price, "Size: …", "Only N left", the sold-out notes, Add to bag
// ("Add to bag — $X", active whenever the choice is in stock), the product form's variant id (with a "change" event)
// and Buy it now (DE.setBuyNow: 'choose' until a size with stock is chosen, then 'ready'; 'hidden' for a sold-out
// variant or colour), then emits "variant:changed" (also kept in DE.productState) for the sticky bar (sticky-atc.js).
// Size prompt (DE.promptSize, variant-picker.js, shared with the featured product, shop-the-pair and quick add):
// Add to bag, the Buy it now catcher and the sticky bar's button ("product:choose-size") pressed before a size is
// chosen send nothing. "Please select a size" goes into the live region under the sizes (emptied first, so a repeat
// is announced again; the size group is described by it), the sizes scroll into view below the pinned header, the
// ones with stock are outlined, and focus moves to the first of them without choosing it. Choosing a size clears all
// of it.
// Add to bag: DE.cart.add; the inline "Added to your bag." confirmation shows for the section's seconds, and the header
// fly-out opens on desktop ("cart:added").
// Also keeps the "de-recently-viewed" list (handles, newest first, at most 7) for sections/recently-viewed.liquid.
const DE = (window.DE = window.DE || {});
const instances = new WeakMap();
const RECENT_KEY = 'de-recently-viewed';
const RECENT_MAX = 7;

const parse = (el) => {
  try {
    return JSON.parse(el?.textContent || 'null');
  } catch {
    return null;
  }
};

/* ---------- Recently viewed ---------- */
const remember = (handle) => {
  if (!handle) return;
  try {
    let list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    if (!Array.isArray(list)) list = [];
    list = [handle, ...list.filter((h) => typeof h === 'string' && h && h !== handle)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // Storage blocked or full: nothing is remembered.
  }
};

/* ---------- Gallery ---------- */
class Gallery {
  constructor(el, grouped) {
    this.el = el;
    this.grouped = grouped;
    this.stack = !!el?.closest('.pmain--stack');
    this.track = el?.querySelector('[data-track]');
    this.slides = el ? [...el.querySelectorAll('[data-media]')] : [];
    this.bars = el?.querySelector('[data-bars]');
    this.shown = this.slides.filter((slide) => !slide.hidden);
    this.observe();
  }

  // A colour's photos plus the ones before any variant photo; every photo when the colour has none.
  groupOf(colour) {
    if (!this.grouped || colour === null) return this.slides;
    const group = this.slides.filter((slide) => !slide.dataset.colour || slide.dataset.colour === colour);
    return group.length ? group : this.slides;
  }

  show(colour) {
    if (!this.el) return;
    const group = this.groupOf(colour);
    if (group.length === this.shown.length && group.every((slide, i) => slide === this.shown[i])) return;
    this.slides.forEach((slide) => (slide.hidden = !group.includes(slide)));
    this.shown = group;
    const count = group.length;
    group.forEach((slide, i) => {
      const wide = i === 0 || (i === count - 1 && i % 2 === 1);
      slide.classList.toggle('is-wide', wide);
      const img = slide.querySelector('img');
      if (img?.sizes) img.sizes = wide || this.stack ? this.el.dataset.sizesWide : this.el.dataset.sizesHalf;
    });
    const first = group[0]?.querySelector('img');
    if (first) first.loading = 'eager';
    if (this.track) this.track.scrollLeft = 0;
    this.renderBars();
    this.observe();
  }

  // Starts loading a colour's first photo (swatch hover or focus).
  warm(colour) {
    if (!this.grouped) return;
    const img = this.groupOf(colour)[0]?.querySelector('img');
    if (img && img.loading === 'lazy') img.loading = 'eager';
  }

  renderBars() {
    if (!this.bars) return;
    const count = this.shown.length;
    this.bars.hidden = count < 2;
    this.track?.classList.toggle('is-single', count < 2);
    this.bars.innerHTML =
      count < 2 ? '' : this.shown.map((_, i) => `<span class="pmain__bar${i === 0 ? ' is-active' : ''}"></span>`).join('');
  }

  setActive(index) {
    if (index < 0 || !this.bars) return;
    [...this.bars.children].forEach((bar, i) => bar.classList.toggle('is-active', i === index));
  }

  observe() {
    this.observer?.disconnect();
    if (!this.track || !('IntersectionObserver' in window) || this.shown.length < 2) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setActive(this.shown.indexOf(entry.target));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.shown.forEach((slide) => this.observer.observe(slide));
  }

  destroy() {
    this.observer?.disconnect();
  }
}

/* ---------- Page ---------- */
class MainProduct {
  constructor(el) {
    this.el = el;
    this.texts = parse(el.querySelector('[data-product-strings]')) || {};
    this.data = parse(el.querySelector('[data-variant-data]'));
    this.addButton = el.querySelector('[data-add]');
    this.idInput = el.querySelector('[data-product-form] [name="id"]');
    this.buyNow = el.querySelector('[data-buy-now]');
    this.info = el.querySelector('[data-info]');
    const gallery = el.querySelector('[data-gallery]');
    this.gallery = new Gallery(gallery, gallery?.dataset.grouped === 'true');

    if (this.data && window.DE?.Picker) {
      this.picker = new DE.Picker(el.querySelector('[data-picker]') || el, this.data, {
        onChange: (picker, what) => this.changed(what),
      });
    }

    this.bind();
    this.update();
    this.stick();
    remember(el.dataset.handle);
  }

  bind() {
    this.el.addEventListener('click', (event) => {
      if (event.target.closest('[data-add]')) this.add();
    });
    // The Buy it now catcher (shown only until a size with stock is chosen) asks for a size and does nothing else.
    this.el.querySelector('[data-buy-now-catch]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.promptSize();
    });
    ['pointerover', 'focusin'].forEach((type) =>
      this.el.addEventListener(type, (event) => {
        const input = event.target.closest?.('.swatch')?.querySelector('[data-colour-input]');
        if (input) this.gallery.warm(input.value);
      })
    );
  }

  changed(what) {
    if (what === 'colour') this.gallery.show(this.picker.colour);
    this.update();
  }

  state() {
    const { picker, data } = this;
    const needsSize = data.sizeIndex !== null;
    const soldOut = !data.available;
    const colourSoldOut =
      !soldOut && !data.v.some((r) => r[3] && (data.colourIndex === null || r[2] === picker.colour));
    let row = null;
    if (!needsSize) row = picker.rowFor(null) || null;
    else if (picker.size !== null) row = picker.rowFor(picker.size) || null;
    const ready = picker.ready;
    const chosenSoldOut = !!row && !row[3];
    return { needsSize, soldOut, colourSoldOut, row, ready, chosenSoldOut, unavailable: soldOut || colourSoldOut || chosenSoldOut };
  }

  update() {
    const { picker, texts, el } = this;
    if (!picker) return;
    const s = this.state();

    const sizeName = el.querySelector('[data-size-name]');
    if (sizeName) sizeName.textContent = picker.size ?? (s.soldOut ? texts.sold_out || 'Sold out' : texts.choose || 'Choose');
    const low = el.querySelector('[data-low]');
    if (low) low.textContent = s.soldOut ? '' : picker.lowText;
    const note = el.querySelector('[data-note]');
    if (note) {
      if (s.soldOut) note.textContent = texts.sold_out_note || '';
      else if (s.colourSoldOut) note.textContent = (texts.colour_note || '').replace('[colour]', picker.colour);
      else note.textContent = '';
    }
    DE.showPrice(el.querySelector('[data-price-block]') || el, picker.price, picker.compareAt);
    this.showError('');
    // A chosen size (or a choice that can't be bought) ends the size prompt; a colour change alone keeps it.
    if (picker.size !== null || s.unavailable) this.clearPrompt();

    // Always "Add to bag — $X" while the choice is in stock, with or without a size (pressed without one, it asks
    // for a size).
    const button = this.addButton;
    if (button && !s.soldOut) {
      button.disabled = s.unavailable;
      button.removeAttribute('aria-disabled');
      button.textContent = s.unavailable
        ? texts.sold_out || 'Sold out'
        : (texts.add || 'Add to bag — [price]').replace('[price]', DE.formatMoney(picker.price));
    }

    // The product form's variant: Shopify's Buy it now buys the variant in its "id" field. Before a size is chosen,
    // the variant whose price shows (the chosen colour's), as the server draws it; Buy it now can't be reached then.
    const formRow = s.row || picker.priceRow;
    if (this.idInput && formRow && this.idInput.value !== String(formRow[0])) {
      this.idInput.value = String(formRow[0]);
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    DE.setBuyNow?.(this.buyNow, s.ready ? 'ready' : s.chosenSoldOut || s.colourSoldOut ? 'hidden' : 'choose');

    this.emit(s);
  }

  emit(s) {
    const { picker, data } = this;
    const detail = {
      handle: data.handle,
      price: picker.price,
      compare: picker.compareAt,
      colour: picker.colour,
      size: picker.size,
      needsSize: s.needsSize,
      variantId: s.ready && s.row ? s.row[0] : null,
      ready: s.ready,
      soldOut: s.soldOut,
      unavailable: s.unavailable,
    };
    DE.productState = detail;
    DE.emit('variant:changed', detail);
  }

  showError(message) {
    const error = this.el.querySelector('[data-error]');
    if (!error) return;
    error.hidden = !message;
    error.textContent = message || '';
  }

  // The size prompt's parts: the size group, the block outlined around it and the live region under it.
  promptParts() {
    const group = this.el.querySelector('[data-size-options]');
    return { group, block: group?.closest('.pmain__sizes') || group, live: this.el.querySelector('[data-size-prompt]') };
  }

  // "Please select a size" (see the header; DE.promptSize, variant-picker.js). Never sends anything and never chooses a
  // size. The sizes are brought into view once the message is in (its line makes the details column taller, which
  // moves a pinned column).
  promptSize() {
    const parts = this.promptParts();
    if (!parts.group || this.picker?.ready) return;
    DE.promptSize({ ...parts, message: this.texts.prompt, reveal: (block) => this.revealSizes(block) });
  }

  clearPrompt() {
    DE.clearSizePrompt(this.promptParts());
  }

  // Scrolls the size block to just below the pinned header (html's scroll-padding-top) when any of it is hidden behind
  // the header, the phone sticky bar or the screen edges.
  // Desktop, sizes hidden above in the sticky details column: scrolling up does not move a pinned column, so the target
  // comes from where the column will sit. If the pinned column shows the sizes, the page scrolls up only until the end
  // of the section (which pushes the column up) leaves them 8px under the header; otherwise it goes back to where the
  // column is not pinned yet, with the sizes 8px under the header.
  revealSizes(block) {
    // The message line has just made the column taller: pin it for its new height now (its ResizeObserver runs later).
    this.place?.();
    const rect = block.getBoundingClientRect();
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 0;
    const bar = document.querySelector('[data-sticky-atc] .satc__bar');
    const bottom = bar?.getClientRects().length ? bar.getBoundingClientRect().top : window.innerHeight;
    if (rect.top >= header - 1 && rect.bottom <= bottom + 1) return;
    const behavior = DE.motionOK() ? 'smooth' : 'auto';
    const info = this.info;
    const style = info && getComputedStyle(info);
    if (rect.top < header - 1 && style?.position === 'sticky') {
      const want = header + 8;
      const offset = rect.top - info.getBoundingClientRect().top;
      const section = this.el.getBoundingClientRect();
      const infoTop = (parseFloat(style.top) || 0) + offset >= want ? section.bottom - info.offsetHeight : section.top;
      window.scrollTo({ top: Math.max(0, window.scrollY + infoTop + offset - want), behavior });
      return;
    }
    block.scrollIntoView({ block: 'start', behavior });
  }

  async add() {
    const { picker, addButton: button } = this;
    if (!picker || !button || button.disabled) return;
    if (!picker.ready) {
      this.promptSize();
      return;
    }
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    this.showError('');
    try {
      const detail = await DE.cart.add([{ id: picker.variant[0], quantity: 1 }], { source: 'product' });
      DE.inlineAdded(this.el.querySelector('[data-added]'), detail.cart, {
        seconds: Number(this.el.dataset.confirm) || 6,
        prefix: this.texts.added,
      });
    } catch (error) {
      this.showError(error.message);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  // Desktop: the sticky details column sits under the pinned header bars, or, when it is taller than the screen,
  // pins by its bottom edge (a negative top).
  stick() {
    const info = this.info;
    if (!info?.classList.contains('pmain__info--sticky') || !('ResizeObserver' in window)) return;
    const place = () => {
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 0;
      const height = info.offsetHeight;
      const top = height > window.innerHeight - header ? window.innerHeight - height : header;
      info.style.setProperty('--pmain-top', `${Math.round(top)}px`);
    };
    this.resizeObserver = new ResizeObserver(place);
    this.resizeObserver.observe(info);
    this.place = place;
    window.addEventListener('resize', place, { passive: true });
  }

  destroy() {
    this.gallery.destroy();
    this.resizeObserver?.disconnect();
    if (this.place) window.removeEventListener('resize', this.place);
  }
}

const init = (scope = document) => {
  scope.querySelectorAll('[data-main-product]').forEach((el) => {
    if (!instances.has(el)) instances.set(el, new MainProduct(el));
  });
};

// The sticky bar's button emits "product:choose-size" (as does DE.promptSize() called with no argument).
DE.on('product:choose-size', () => {
  const el = document.querySelector('[data-main-product]');
  if (el) instances.get(el)?.promptSize();
});

document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-main-product]').forEach((el) => {
    instances.get(el)?.destroy();
    instances.delete(el);
  });
});

init();
